import React from 'react'
import { useSelector } from 'react-redux'
import { BrowserRouter, Redirect, Route, Switch, useLocation } from 'react-router-dom'

import { URL_CONST } from 'utils/urlConsts'

import App from 'containers/App'
import {
  selectActualFeatureFlags,
  selectIsCustomerAccessRestricted,
} from 'containers/App/selectors'
import EULAModal from 'containers/EULAModal'
import FirstLoginModal from 'containers/FirstLoginModal'
import GlobalBanner from 'containers/GlobalBanner'
import Login from 'containers/Login'
import Navigation from 'containers/Navigation'
import NewRelease from 'containers/NewRelease'
import Reintegration from 'containers/Reintegration'
import SpearPhishingAgent from 'containers/SpearPhishingAgent'
import ToastsContainer from 'containers/Toasts'

import { renderComponentWithDataLayer } from '../utils/asyncReducer'

import { ThemeDocumentSync, ThemeProvider } from './context/ThemeContext'
import TooltipProvider from './context/TooltipContext'
import AppLayout from './layouts/AppLayout'
import PlatformNavLayout from './layouts/PlatformNavLayout'
import CreditNotificationsManager from './pages/CreditNotificationsManager'
import KpiPage from './pages/KpiPage'
import Placeholder from './pages/Placeholder'
import RefreshErrorScreen from './pages/RefreshErrorScreen'
import RestrictedAccess from './pages/RestrictedAccess'
import { RootStyles } from './styles/root.styles'
import PendoVisitorSync from './PendoVisitorSync'

import '@ironscales/design-system/styles'
import './styles/host-tokens'

const handleChunkLoadError = () => {
  window.DISABLE_SENTRY = true
  return { default: RefreshErrorScreen }
}

const Activities = React.lazy(() => import('../containers/Activities').catch(handleChunkLoadError))
const AwarenessCenter = React.lazy(() =>
  import('../containers/AwarenessCenter').catch(handleChunkLoadError)
)
const Dashboard = React.lazy(() => import('../containers/Dashboard').catch(handleChunkLoadError))
const Deepfake = React.lazy(() => import('../containers/Deepfake').catch(handleChunkLoadError))
const Mailboxes = React.lazy(() => import('../containers/Mailboxes').catch(handleChunkLoadError))
const Partner = React.lazy(() => import('../containers/Partner').catch(handleChunkLoadError))
const ResolutionCenter = React.lazy(() =>
  import('../containers/ResolutionCenter').catch(handleChunkLoadError)
)
const Resources = React.lazy(() => import('../containers/Resources').catch(handleChunkLoadError))
const Settings = React.lazy(() => import('../containers/Settings').catch(handleChunkLoadError))
const GroupSettings = React.lazy(() =>
  import('../containers/GroupSettings').catch(handleChunkLoadError)
)

const ThreatLog = React.lazy(() =>
  import('../containers/Investigation').catch(handleChunkLoadError)
)
const DMARC = React.lazy(() => import('../containers/DMARC').catch(handleChunkLoadError))
const EncryptionPLG = React.lazy(() =>
  import('../containers/EncryptionPLG').catch(handleChunkLoadError)
)
const RedTeam = React.lazy(() => import('../containers/RedTeam').catch(handleChunkLoadError))
const VendorProtection = React.lazy(() =>
  import('./pages/vendorProtection').catch(handleChunkLoadError)
)
const EmailEncryptionOES = React.lazy(() =>
  import('../containers/EmailEncryption').catch(handleChunkLoadError)
)
const SocAgentDashboard = React.lazy(() =>
  import('./pages/agents/SocAgentDashboard').catch(handleChunkLoadError)
)

require('./styles/antd.css')

// Routes that remain accessible even when access is restricted
const ALLOWED_PATHS_WHEN_RESTRICTED = [
  URL_CONST.mailboxes.self,
  URL_CONST.settings.self,
  URL_CONST.restrictedAccess.self,
]

const isPathAllowedWhenRestricted = (pathname) =>
  ALLOWED_PATHS_WHEN_RESTRICTED.some((path) => pathname.startsWith(path))

/**
 * Shell — chooses the app chrome based on the NEW_PLATFORM_NAV flag.
 *
 * Flag ON  → PlatformNavLayout (SideNav + TopBar; renders its own nav chrome).
 * Flag OFF → existing AppLayout with the legacy <Navigation/> (instant rollback).
 *
 * The old <Navigation/> is intentionally left in place so both shells coexist
 * until the flag defaults on.
 */
const Shell = ({ children }) => {
  const ff = useSelector(selectActualFeatureFlags)

  if (ff?.NEW_PLATFORM_NAV) {
    return <PlatformNavLayout banner={<GlobalBanner />}>{children}</PlatformNavLayout>
  }

  return (
    <AppLayout banner={<GlobalBanner />} navigation={<Navigation />}>
      {children}
    </AppLayout>
  )
}

const RestrictedRouteGuard = ({ children }) => {
  const isAccessRestricted = useSelector(selectIsCustomerAccessRestricted)
  const location = useLocation()

  if (isAccessRestricted && !isPathAllowedWhenRestricted(location.pathname)) {
    return <Redirect to={URL_CONST.restrictedAccess.self} />
  }

  return children
}

const RootContainer = ({ store }) => {
  const render = renderComponentWithDataLayer(store)
  return (
    <>
      <RootStyles />
      <ThemeProvider>
        <TooltipProvider>
          <BrowserRouter>
            <ThemeDocumentSync />
            <PendoVisitorSync />
            <Switch>
              <Route path={URL_CONST.login.signin} component={render(Login, ['Login'])} />
              <Route path="*">
                <App>
                  <Shell>
                    <NewRelease />
                    <Reintegration />
                    <FirstLoginModal />
                    <EULAModal />

                    <React.Suspense fallback={<Placeholder />}>
                      <RestrictedRouteGuard>
                        <Switch>
                          <Route
                            path={URL_CONST.restrictedAccess.self}
                            component={RestrictedAccess}
                          />
                          <Route
                            path={URL_CONST.activities.self}
                            component={render(Activities, ['Activities'])}
                          />
                          <Route
                            path={URL_CONST.resources.self}
                            component={render(Resources, ['Resources'])}
                          />
                          <Route
                            path={URL_CONST.investigation.threatLog}
                            component={render(ThreatLog, ['Investigation', 'Partner'])}
                          />
                          <Route
                            path={URL_CONST.dashboard.home}
                            component={render(Dashboard, ['Dashboard'])}
                          />
                          <Route
                            path={URL_CONST.mailboxes.self}
                            component={render(Mailboxes, ['Mailboxes', 'CustomizedAutoSync'])}
                          />
                          <Route
                            path={URL_CONST.incidents.self}
                            component={render(ResolutionCenter, ['ResolutionCenter'])}
                          />
                          <Route
                            path={URL_CONST.awarenessCenter.self}
                            component={render(AwarenessCenter, ['AwarenessCenter'], { store })}
                          />
                          <Route
                            path={URL_CONST.deepfake.self}
                            component={render(Deepfake, ['Deepfake'])}
                          />
                          <Route
                            path={URL_CONST.psima.self}
                            component={render(SpearPhishingAgent, ['SpearPhishingAgent'])}
                          />
                          <Redirect
                            from={URL_CONST.incidents.partners}
                            to={URL_CONST.incidents.self}
                          />
                          <Route
                            path={URL_CONST.partners.self}
                            component={render(Partner, ['Partner'])}
                          />
                          <Route
                            path={URL_CONST.settings.self}
                            component={render(Settings, [
                              'Settings',
                              'CustomizedAutoSync',
                              'GroupSettings',
                            ])}
                          />
                          <Route path={URL_CONST.kpi.self} component={KpiPage} />
                          <Route
                            path={URL_CONST.groupSettings.self}
                            component={render(GroupSettings, ['GroupSettings', 'Settings'])}
                          />
                          <Route path={URL_CONST.dmarc.self} component={render(DMARC, ['DMARC'])} />
                          <Route
                            path={URL_CONST.encryptionPLG.self}
                            component={render(EncryptionPLG, ['EncryptionPLG'])}
                          />
                          <Route
                            path={URL_CONST.redTeam.self}
                            component={render(RedTeam, ['RedTeam'])}
                          />
                          <Redirect
                            from={URL_CONST.vendorBase.legacy}
                            to={URL_CONST.vendorBase.self}
                          />
                          <Route
                            path={URL_CONST.vendorBase.self}
                            component={render(VendorProtection, ['VendorProtection'])}
                          />
                          <Route
                            path={URL_CONST.emailEncryption.self}
                            component={render(EmailEncryptionOES, ['EmailEncryption', 'Settings'])}
                          />
                          <Route
                            path={URL_CONST.agents.socDashboard}
                            component={SocAgentDashboard}
                          />
                        </Switch>
                      </RestrictedRouteGuard>
                    </React.Suspense>
                  </Shell>
                </App>
              </Route>
            </Switch>
            <CreditNotificationsManager />
            <ToastsContainer />
          </BrowserRouter>
        </TooltipProvider>
      </ThemeProvider>
    </>
  )
}

export default RootContainer
